import { useState, useEffect, useRef } from 'react';
import { AppData, App, APP_EVENTS } from '../types/museum/museum';
import { threeJSManager } from '../utils/threeJSManager';

export const useHallApp = (containerRef: React.RefObject<HTMLDivElement>, setIsSceneLoaded: (value: boolean) => void) => {
  const [app, setApp] = useState<App | undefined>(undefined);
  const isMounted = useRef(false);
  const appEventsBus = useRef<any>(null);
  const initializeAttempts = useRef(0);
  const MAX_INITIALIZE_ATTEMPTS = 5;

  useEffect(() => {
    if (!containerRef.current) return;
    
    isMounted.current = true;

    const cleanup = async () => {
      // console.log('Cleaning up Hall app...');
      
      if (appEventsBus.current) {
        Object.values(APP_EVENTS).forEach(eventName => {
          appEventsBus.current.removeAllListeners?.(eventName);
        });
      }

      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }

      setApp(undefined);
      appEventsBus.current = null;
      setIsSceneLoaded(false);
    };

    const initializeApp = async () => {
      if (!isMounted.current) return;

      if (!window.three_app?.start) {
        // console.log(`ThreeJS app not available, retrying... (attempt ${initializeAttempts.current + 1}/${MAX_INITIALIZE_ATTEMPTS})`);
        if (initializeAttempts.current < MAX_INITIALIZE_ATTEMPTS) {
          initializeAttempts.current++;
          setTimeout(initializeApp, 1000);
        } else {
          console.error('Failed to initialize after maximum attempts');
          setIsSceneLoaded(false);
        }
        return;
      }

      try {
        // console.log('Starting new Hall ThreeJS instance...');
        const appData: AppData = await window.three_app.start();
        
        if (!isMounted.current) return;

        if (!appData?.app) {
          // console.log('App data not available, retrying...');
          if (initializeAttempts.current < MAX_INITIALIZE_ATTEMPTS) {
            initializeAttempts.current++;
            setTimeout(initializeApp, 1000);
          }
          return;
        }

        // Проверяем, что appEventsBus существует
        if (!appData.app.appEventsBus) {
          console.error('App event bus not available');
          return;
        }

        appEventsBus.current = appData.app.appEventsBus;
        setApp(appData.app);
        setIsSceneLoaded(true);

        // Добавляем обработчик для SCENE_READY
        appEventsBus.current.on(APP_EVENTS.SCENE_READY, (sceneName: string) => {
          if (!isMounted.current) return;
          // console.log('Hall SCENE_READY:', sceneName);
          if (sceneName === 'hall') {
            setIsSceneLoaded(true);
          }
        });

        if (window.three_app?.restoreCanvas) {
          window.three_app.restoreCanvas();
        }
      } catch (error) {
        console.error('Error initializing Hall app:', error);
        if (initializeAttempts.current < MAX_INITIALIZE_ATTEMPTS) {
          initializeAttempts.current++;
          setTimeout(initializeApp, 1000);
        }
      }
    };

    const startSequence = async () => {
      try {
        await cleanup();
        initializeAttempts.current = 0;
        
        // Загружаем скрипт
        await threeJSManager.loadScript('hall');
        
        // Увеличиваем время ожидания
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        if (isMounted.current) {
          // Добавляем проверку на существование three_app
          if (!window.three_app?.start) {
            console.error('three_app not initialized after script load');
            setIsSceneLoaded(false);
            return;
          }
          
          await initializeApp();
        }
      } catch (error) {
        console.error('Error in Hall start sequence:', error);
        setIsSceneLoaded(false);
      }
    };

    startSequence();

    return () => {
      isMounted.current = false;
      cleanup();
    };
  }, [containerRef, setIsSceneLoaded]);

  return app;
};